import dayjs, { Dayjs } from 'dayjs';

/**
 * Elemento con riferimento a entità e data (compatibile con ValueWithEntityReference).
 */
export interface SourceValueItem {
  entityId: string;
  value: unknown;
  month?: Date | string | Dayjs;
  date?: Date | string | Dayjs;
}

/**
 * Costruisce valori annui filtrati per periodo di riferimento: per ogni (entityId, year)
 * somma e conta solo i mesi compresi in [periodFrom, periodTo].
 * Utilizzato da Contatori, Variabili pertinenti, Consumi e costi e Indicatori per la griglia "Periodo di riferimento".
 *
 * @param sourceValues - Dati mensili/dettagliati con entityId e data
 * @param periodFrom - Inizio periodo (es. 01/07/2023)
 * @param periodTo - Fine periodo (es. 30/06/2024)
 * @param entityIds - Elenco ID entità da includere
 * @param getValueToSum - Estrae il numero da sommare da ogni item (default: (item.value as any)?.value ?? 0)
 * @param buildValue - Costruisce il value da (entityId, year, sum, count, context); count = numero di mesi con dato
 * @returns Array { entityId, date: 1 gen year, value } per ogni entità e anno nel range
 */
export function buildReferencePeriodYearlyValues<T, C = void>(params: {
  sourceValues: SourceValueItem[];
  periodFrom: Date | string;
  periodTo: Date | string;
  entityIds: string[];
  getValueToSum?: (item: SourceValueItem) => number;
  buildValue: (entityId: string, year: number, sum: number, count: number, context: C) => T;
  context?: C;
}): Array<{ entityId: string; date: Date; value: T }> {
  const {
    sourceValues,
    periodFrom,
    periodTo,
    entityIds,
    getValueToSum = item => Number((item.value as any)?.value ?? 0),
    buildValue,
    context,
  } = params;

  const ctx = context as C;

  if (!sourceValues.length) return [];

  const refFrom = dayjs(periodFrom).startOf('month');
  const refTo = dayjs(periodTo).endOf('month');
  const fromYear = refFrom.year();
  const toYear = refTo.year();
  const years: number[] = [];
  for (let y = fromYear; y <= toYear; y++) years.push(y);

  const sumByEntityYear = new Map<string, number>();
  const countByEntityYear = new Map<string, number>();
  sourceValues.forEach(item => {
    const d = dayjs(item.month ?? item.date).startOf('month');
    if (d.isBefore(refFrom) || d.isAfter(refTo)) return;
    const year = d.year();
    if (year < fromYear || year > toYear) return;
    const key = `${item.entityId}-${year}`;
    sumByEntityYear.set(key, (sumByEntityYear.get(key) ?? 0) + getValueToSum(item));
    countByEntityYear.set(key, (countByEntityYear.get(key) ?? 0) + 1);
  });

  const result: Array<{ entityId: string; date: Date; value: T }> = [];
  entityIds.forEach(entityId => {
    years.forEach(year => {
      const key = `${entityId}-${year}`;
      const sum = sumByEntityYear.get(key) ?? 0;
      const count = countByEntityYear.get(key) ?? 0;
      result.push({
        entityId,
        date: new Date(year, 0, 1),
        value: buildValue(entityId, year, sum, count, ctx),
      });
    });
  });
  return result;
}
